import {
  Recommender,
  Recommenders,
  Talent,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import { ContentKey } from "../TalentDetails";
import { Backdrop, Box, Grid, Typography } from "@mui/material";
import FormTable from "../../../common/components/functional/formTable/FormTable";
import isEmpty from "../../../common/helpers/isEmpty";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useContext, useState } from "react";
import {
  selectTalentById,
  updateTalentRecommenders,
} from "../../talents/talentsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import FormattedPhone from "../../../common/components/functional/phoneField/FormattedPhone";
import { AccessRightContext } from "../../user/AccessRightProvider";

type Props = {
  recommenders?: Recommenders;
  handleOpenEditModal: (content: ContentKey) => void;
};

const formDataFrom = (recommenders: Recommender[]) =>
  recommenders.map((recommender) => ({
    values: {
      recommender: (
        <>
          <Box>{recommender.name}</Box>
          <Box sx={{ fontWeight: 500 }}>
            {[recommender.title, recommender.company].join(", ")}
          </Box>
        </>
      ),
      contactInfo: (
        <>
          <FormattedPhone value={recommender.phone} />
          <Box>{recommender.email}</Box>
        </>
      ),
    },
    id: recommender.id,
    deletable: true,
  }));

const TalentRecommenders = ({ recommenders, handleOpenEditModal }: Props) => {
  const { t } = useTranslation();
  const { talentId } = useParams();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { canEditTalentProfile } = useContext(AccessRightContext);
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const deleteRecommender = (id: string) => {
    const currentValues = talent.recommenders?.values || [];
    const currentVersion = talent.recommenders?.version || 0;

    if (talentId && !isEmpty(currentValues) && currentVersion) {
      setIsLoading(true);

      const newValues = currentValues.filter((value) => value.id !== id);

      const commandPayload = {
        talentId: talentId,
        version: currentVersion,
        recommenders: newValues,
      };

      dispatch(updateTalentRecommenders(commandPayload)).then((action) => {
        setIsLoading(false);
        return isFulfilled(action);
      });
    }
  };

  const title = t("recommenders:recommenders");
  const headers = [
    { text: t("recommenders:nameTitleCompany") },
    { text: t("recommenders:contactInfo") },
  ];
  const addButtonLabel = t("recommenders:add");

  const hasRecommenders = !!recommenders && recommenders.values.length > 0;
  const formData = hasRecommenders ? formDataFrom(recommenders.values) : [];

  return (
    <>
      <Backdrop
        open={isLoading}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          zIndex: 1000,
        }}
      >
        <LoadingSpinner />
      </Backdrop>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {title}
        </Typography>
      </Grid>
      <Grid item container xs={9}>
        <FormTable
          headers={headers}
          data={formData}
          contentKey="recommenders"
          handleOpenEditModal={handleOpenEditModal}
          addButtonLabel={addButtonLabel}
          handleDelete={deleteRecommender}
          disableEditRow={!canEditTalentProfile || talentIsDeactivated(talent)}
        />
      </Grid>
    </>
  );
};

export default TalentRecommenders;
